// src/components/ModalCarrito.jsx
import React from "react";
import { createPortal } from "react-dom";
import { useCarrito } from "../context/CarritoContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function ModalCarrito({ cerrar }) {
  const navigate = useNavigate();
  const {
    carrito,
    aumentarCantidad,
    disminuirCantidad,
    eliminarProducto,
    vaciarCarrito,
    cambiarModoVenta,
  } = useCarrito();

  // JSX del modal
  const modal = (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={cerrar}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border-2 border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="border-b p-4 flex justify-between items-center bg-white sticky top-0 z-10">
          <h2 className="text-xl font-bold text-sky-800">Tu Carrito</h2>
          <button
            onClick={cerrar}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            ✕
          </button>
        </div>

        {/* Contenido */}
        <div className="overflow-y-auto flex-1 p-4">
          {carrito.length === 0 ? (
            <p className="text-center text-gray-500">No hay productos.</p>
          ) : (
            <ul className="space-y-4">
              {carrito.map((item) => (
                <li
                  key={item.sku}
                  className="flex gap-3 items-start pb-4 border-b last:border-0"
                >
                  {item.imagen && (
                    <img
                      src={item.imagen}
                      alt={item.nombre}
                      className="w-20 h-20 object-contain rounded-lg flex-shrink-0 bg-white"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#40767b]">
                      {item.nombre}
                      {item.modoVenta && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({item.modoVenta})
                        </span>
                      )}
                    </p>
                    
                    {item.modoFardo && (
                      <button
                        onClick={() => cambiarModoVenta(item.sku)}
                        className="text-xs text-sky-500 hover:underline mt-1"
                      >
                        Cambiar a {item.modoVenta === "fardo" ? "individual" : "fardo"}
                      </button>
                    )}
                    {item.descripcion && (
                      <p className="text-sm text-gray-600">
                        {item.descripcion}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => disminuirCantidad(item.sku)}
                        disabled={item.cantidad <= 1}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        value={item.cantidad}
                        onChange={(e) => {
                          const nuevaCantidad = parseInt(e.target.value);
                          if (!isNaN(nuevaCantidad) && nuevaCantidad >= 1) {
                            aumentarCantidad(item.sku, nuevaCantidad);
                          }
                        }}
                        className="w-16 text-center font-medium border border-gray-300 rounded-md text-sm px-1 py-1"
                      />
                      <button
                        onClick={() => aumentarCantidad(item.sku)}
                        disabled={item.cantidad >= (item.stock || Infinity)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                      >
                        +
                      </button>
                      <button
                        onClick={() => eliminarProducto(item.sku)}
                        className="ml-auto text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {carrito.length > 0 && (
          <div className="border-t p-4 bg-white sticky bottom-0">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={vaciarCarrito}
                  className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Vaciar carrito
                </button>
                <button
                  onClick={() => {
                    cerrar();
                    const token = Date.now().toString(36); // siempre distinto
                
                    if (location.pathname === "/cotizar") {
                      // Ya estás en /cotizar → cambia solo la query+hash para disparar el efecto
                      navigate(`?scroll=${token}#cotizar-top`, {
                        state: { forceScroll: token },
                      });
                    } else {
                      // Vienes de otra ruta → navega a /cotizar con query+hash
                      navigate(`/cotizar?scroll=${token}#cotizar-top`, {
                        state: { forceScroll: token },
                      });
                    }
                  }}
                  className="flex-1 py-2 bg-[#66959a] text-white rounded-lg hover:bg-[#40767b] flex items-center justify-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Cotizar ahora
                </button>
              </div>
              <button
                onClick={cerrar}
                className="w-full py-2 text-sky-600 hover:text-sky-800 text-sm flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Seguir comprando
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Monta el modal directamente en <body> para que no lo recorte ningún ancestro
  return createPortal(modal, document.body);
}

